import { useState, useEffect, useMemo } from 'react';
import {
  type PreviewKind,
  detectPreviewKind,
  extractHex,
  matchFontFamily,
  ensureFontLoaded,
} from './agentOptionPreview';

export interface AgentOption {
  type: 'radio' | 'checkbox';
  question?: string;
  options: string[];
  allowOther?: boolean;
  /**
   * Optional hint from the agent that the option labels carry a visual
   * value. When omitted we fall back to auto-detection on the labels —
   * #RRGGBB substrings imply 'colour', recognised font names imply 'font'.
   * Honouring the explicit signal first lets the agent disambiguate
   * borderline cases (e.g. options labelled "Inter" outside a font
   * question).
   */
  previewKind?: PreviewKind;
}

interface Props {
  option: AgentOption;
  onSend: (content: string) => void;
  disabled?: boolean;
  /** Previously selected value(s) — shown on session resume */
  selectedValues?: string[];
}

export function parseAgentOptions(content: string): AgentOption[] {
  const results: AgentOption[] = [];
  const regex = /<!--\s*AGENT_OPTIONS:\s*(\{[\s\S]*?\})\s*-->/g;
  let match;
  while ((match = regex.exec(content)) !== null) {
    try {
      const parsed = JSON.parse(match[1]);
      if (parsed.options && Array.isArray(parsed.options) && parsed.options.length > 0) {
        // Codex P2 on PR #689: only accept the agent-facing values
        // ("colour" / "color" / "font"). Any other string — including
        // "none", typos, or future drift — normalises to undefined so
        // the consumer's `?? detectPreviewKind(...)` fallback still
        // runs and the option list still gets the correct preview.
        // "none" is an internal detection result, NOT an agent input;
        // letting the agent explicitly suppress previews on a clearly
        // visual question would just hide swatches when something
        // upstream went wrong.
        let previewKind: PreviewKind | undefined;
        if (parsed.previewKind === 'colour' || parsed.previewKind === 'color') {
          previewKind = 'colour';
        } else if (parsed.previewKind === 'font') {
          previewKind = 'font';
        } else {
          previewKind = undefined;
        }
        results.push({
          type: parsed.type === 'checkbox' ? 'checkbox' : 'radio',
          question: parsed.question ?? undefined,
          options: parsed.options,
          allowOther: parsed.allowOther ?? false,
          previewKind,
        });
      }
    } catch { /* malformed JSON — skip */ }
  }
  return results;
}

export default function ChatOptionWidget({ option, onSend, disabled, selectedValues }: Props) {
  const [selected, setSelected] = useState<Set<string>>(new Set(selectedValues ?? []));
  const [otherText, setOtherText] = useState('');
  const [otherActive, setOtherActive] = useState(false);
  const [sent, setSent] = useState(!!selectedValues?.length);

  const isRadio = option.type === 'radio';
  const isDisabled = disabled || sent;

  // Resolve previewKind: explicit hint from the agent wins; otherwise
  // auto-detect across the option labels so the renderer never flips
  // styles halfway down a list.
  const previewKind: PreviewKind = useMemo(
    () => option.previewKind ?? detectPreviewKind(option.options),
    [option.previewKind, option.options],
  );

  // Lazy-load Google Fonts for any font-preview options on first render.
  useEffect(() => {
    if (previewKind !== 'font') return;
    for (const label of option.options) {
      const entry = matchFontFamily(label);
      if (entry) ensureFontLoaded(entry);
    }
  }, [previewKind, option.options]);

  // Initial value for the native colour picker on the "Something else"
  // path. Defaults to the brand primary so the picker opens somewhere
  // sensible rather than pure black.
  const [otherColour, setOtherColour] = useState('#7C3AED');

  const handleRadioClick = (value: string) => {
    if (isDisabled) return;
    setSelected(new Set([value]));
    setSent(true);
    onSend(value);
  };

  const handleCheckboxToggle = (value: string) => {
    if (isDisabled) return;
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      // Deselect "other" text if unchecking
      if (value === '__other__' && next.has(value)) setOtherActive(true);
      if (value === '__other__' && !next.has(value)) setOtherActive(false);
      return next;
    });
  };

  const handleOtherRadio = () => {
    if (isDisabled) return;
    setOtherActive(true);
    setSelected(new Set(['__other__']));
  };

  const handleOtherSubmit = () => {
    if (!otherText.trim() || isDisabled) return;
    setSent(true);
    onSend(otherText.trim());
  };

  const handleCheckboxConfirm = () => {
    if (selected.size === 0 || isDisabled) return;
    const values = [...selected].map(v => v === '__other__' ? otherText.trim() : v).filter(Boolean);
    if (values.length === 0) return;
    setSent(true);
    onSend(values.join(', '));
  };

  const pillBase: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '8px 14px', borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--border)', background: 'var(--surface-alt)',
    fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)',
    cursor: isDisabled ? 'default' : 'pointer',
    transition: 'all 0.15s ease',
    opacity: isDisabled && !sent ? 0.5 : 1,
  };

  const pillSelected: React.CSSProperties = {
    ...pillBase,
    borderColor: 'var(--primary)',
    background: 'rgba(124,58,237,0.06)',
    fontWeight: 600,
  };

  const indicator = (isSelected: boolean) => (
    <span style={{
      width: 16, height: 16, borderRadius: isRadio ? '50%' : 3,
      border: `2px solid ${isSelected ? 'var(--primary)' : 'var(--border)'}`,
      background: isSelected ? 'var(--primary)' : 'transparent',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0, transition: 'all 0.15s ease',
    }}>
      {isSelected && (
        <span style={{ color: 'white', fontSize: 10, lineHeight: 1 }}>
          {isRadio ? '\u2022' : '\u2713'}
        </span>
      )}
    </span>
  );

  /**
   * Per-option visual preview rendered between the radio/checkbox indicator
   * and the label text. Returns:
   *   - colour swatch for hex-bearing labels under previewKind === 'colour'
   *   - placeholder otherwise (so all rows align vertically inside a
   *     colour question, even when one option is "Derive automatically")
   *   - null when the question carries no preview at all
   */
  const renderColourSwatch = (label: string) => {
    const hex = extractHex(label);
    if (!hex) {
      // Keep alignment with peer rows that DO have a swatch.
      return <span aria-hidden="true" style={{ width: 18, height: 18, flexShrink: 0 }} />;
    }
    return (
      <span
        data-testid="option-colour-swatch"
        aria-hidden="true"
        title={hex}
        style={{
          width: 18, height: 18, borderRadius: 4,
          background: hex,
          border: '1px solid var(--border)',
          flexShrink: 0,
          boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
        }}
      />
    );
  };

  /**
   * Render the option label in its own font face when the question is a
   * font-family pick. Returns the rendered label (or the plain label
   * when the family isn't recognised, so unrecognised "Something else"-
   * style options still display clearly).
   */
  const renderFontLabel = (label: string) => {
    const entry = matchFontFamily(label);
    if (!entry) {
      return <span>{label}</span>;
    }
    return (
      <span
        data-testid="option-font-preview"
        data-font-family={entry.name}
        style={{ fontFamily: entry.cssStack, fontSize: 14 }}
      >
        {label}
      </span>
    );
  };

  return (
    <div style={{ marginTop: 10 }}>
      {option.question && (
        <p style={{
          fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600,
          color: 'var(--text-secondary)', marginBottom: 6,
          textTransform: 'uppercase', letterSpacing: '0.03em',
        }}>
          {option.question}
        </p>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {option.options.map((opt) => {
          const isSelected = selected.has(opt);
          return (
            <div
              key={opt}
              role={isRadio ? 'radio' : 'checkbox'}
              aria-checked={isSelected}
              tabIndex={isDisabled ? -1 : 0}
              style={isSelected ? pillSelected : pillBase}
              onClick={() => { if (isRadio) handleRadioClick(opt); else handleCheckboxToggle(opt); }}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  if (isRadio) handleRadioClick(opt); else handleCheckboxToggle(opt);
                }
              }}
              onMouseEnter={e => { if (!isDisabled) e.currentTarget.style.borderColor = 'var(--primary)'; }}
              onMouseLeave={e => { if (!isDisabled && !isSelected) e.currentTarget.style.borderColor = 'var(--border)'; }}
            >
              {indicator(isSelected)}
              {previewKind === 'colour' && renderColourSwatch(opt)}
              {previewKind === 'font' ? renderFontLabel(opt) : <span>{opt}</span>}
              {isSelected && sent && (
                <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--primary)', fontWeight: 600 }}>
                  Sent
                </span>
              )}
            </div>
          );
        })}

        {/* "Something else..." option */}
        {option.allowOther && (
          <div>
            <div
              role={isRadio ? 'radio' : 'checkbox'}
              aria-checked={selected.has('__other__')}
              tabIndex={isDisabled ? -1 : 0}
              style={selected.has('__other__') ? pillSelected : pillBase}
              onClick={() => { if (isRadio) handleOtherRadio(); else handleCheckboxToggle('__other__'); }}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  if (isRadio) handleOtherRadio(); else handleCheckboxToggle('__other__');
                }
              }}
              onMouseEnter={e => { if (!isDisabled) e.currentTarget.style.borderColor = 'var(--primary)'; }}
              onMouseLeave={e => { if (!isDisabled && !selected.has('__other__')) e.currentTarget.style.borderColor = 'var(--border)'; }}
            >
              {indicator(selected.has('__other__'))}
              <span>Something else...</span>
            </div>
            {otherActive && !sent && previewKind === 'colour' && (
              <div
                data-testid="option-colour-picker"
                style={{ display: 'flex', gap: 6, marginTop: 4, paddingLeft: 24, alignItems: 'center' }}
              >
                <input
                  type="color"
                  aria-label="Pick a custom colour"
                  value={otherColour}
                  onChange={e => {
                    setOtherColour(e.target.value);
                    // Mirror the picked value into the text field so the
                    // user can fine-tune it (or paste a brand-mandated
                    // hex). The text field is the source of truth on
                    // submit, so picking-then-editing always works.
                    setOtherText(e.target.value.toUpperCase());
                  }}
                  style={{
                    width: 40, height: 36, padding: 0, border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-sm)', background: 'var(--surface)',
                    cursor: 'pointer',
                  }}
                />
                <input
                  type="text"
                  value={otherText}
                  onChange={e => setOtherText(e.target.value)}
                  placeholder="#RRGGBB"
                  autoFocus
                  style={{
                    flex: 1, padding: '6px 10px', borderRadius: 'var(--radius-sm)',
                    border: '1px solid var(--border)', background: 'var(--surface)',
                    fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)',
                    outline: 'none',
                  }}
                  onFocus={e => { e.currentTarget.style.borderColor = 'var(--primary)'; }}
                  onBlur={e => { e.currentTarget.style.borderColor = 'var(--border)'; }}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleOtherSubmit(); } }}
                />
                <button
                  onClick={handleOtherSubmit}
                  disabled={!otherText.trim()}
                  style={{
                    padding: '6px 14px', borderRadius: 'var(--radius-sm)',
                    background: otherText.trim() ? 'var(--primary)' : 'var(--surface-alt)',
                    color: otherText.trim() ? 'white' : 'var(--text-secondary)',
                    border: 'none', fontFamily: 'var(--font-body)', fontSize: 12,
                    fontWeight: 600, cursor: otherText.trim() ? 'pointer' : 'default',
                  }}
                >
                  Send
                </button>
              </div>
            )}
            {otherActive && !sent && previewKind !== 'colour' && (
              <div style={{ display: 'flex', gap: 6, marginTop: 4, paddingLeft: 24 }}>
                <input
                  type="text"
                  value={otherText}
                  onChange={e => setOtherText(e.target.value)}
                  placeholder="Type your answer..."
                  autoFocus
                  style={{
                    flex: 1, padding: '6px 10px', borderRadius: 'var(--radius-sm)',
                    border: '1px solid var(--border)', background: 'var(--surface)',
                    fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)',
                    outline: 'none',
                  }}
                  onFocus={e => { e.currentTarget.style.borderColor = 'var(--primary)'; }}
                  onBlur={e => { e.currentTarget.style.borderColor = 'var(--border)'; }}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleOtherSubmit(); } }}
                />
                <button
                  onClick={handleOtherSubmit}
                  disabled={!otherText.trim()}
                  style={{
                    padding: '6px 14px', borderRadius: 'var(--radius-sm)',
                    background: otherText.trim() ? 'var(--primary)' : 'var(--surface-alt)',
                    color: otherText.trim() ? 'white' : 'var(--text-secondary)',
                    border: 'none', fontFamily: 'var(--font-body)', fontSize: 12,
                    fontWeight: 600, cursor: otherText.trim() ? 'pointer' : 'default',
                  }}
                >
                  Send
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Checkbox confirm button */}
      {!isRadio && !sent && selected.size > 0 && !otherActive && (
        <button
          onClick={handleCheckboxConfirm}
          style={{
            marginTop: 8, padding: '7px 18px', borderRadius: 'var(--radius-sm)',
            background: 'var(--primary)', color: 'white', border: 'none',
            fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Confirm ({selected.size} selected)
        </button>
      )}
      {/* Checkbox confirm when "other" is active */}
      {!isRadio && !sent && selected.size > 0 && otherActive && (
        <button
          onClick={handleCheckboxConfirm}
          disabled={selected.has('__other__') && !otherText.trim()}
          style={{
            marginTop: 8, padding: '7px 18px', borderRadius: 'var(--radius-sm)',
            background: (selected.has('__other__') && !otherText.trim()) ? 'var(--surface-alt)' : 'var(--primary)',
            color: (selected.has('__other__') && !otherText.trim()) ? 'var(--text-secondary)' : 'white',
            border: 'none', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600,
            cursor: (selected.has('__other__') && !otherText.trim()) ? 'default' : 'pointer',
          }}
        >
          Confirm ({selected.size} selected)
        </button>
      )}
    </div>
  );
}
